import { toast } from "@/components/common/Toast"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { apiErrorMessage } from "../../api/apiError"
import { uploadDeclarationFile } from "../../api/declarationsApi"
import { isAcceptedDeclarationFile } from "./declarationFileTypes"

// EVERY ACCEPTED UPLOAD STARTS AN EXTRACTION RUN, and a dense document holds that
// run for minutes. Firing a 50-file folder off in one go would put 50 of them on
// Bedrock at once and hold 50 request bodies (50 MB each, read whole) in backend
// memory. The browser's own ~6-connections-per-host cap would blunt the first
// problem and neither of the others, so the queue is bounded here instead. Low
// enough to be gentle, high enough that the wall-clock cost is a rounding error.
export const UPLOAD_CONCURRENCY = 3

export type ImportState = "queued" | "uploading" | "done" | "failed"

export interface ImportRow {
  /** Stable across the run: a folder can hold two files of the same name. */
  key: string
  name: string
  state: ImportState
  /** Why it failed, in the words of whoever refused it. */
  error?: string
}

export interface ImportOutcome {
  total: number
  succeeded: number
  failed: number
}

/**
 * Why the backend refused this file, in OUR words: a coded refusal maps to
 * its own copy (`UNSUPPORTED_FILE_TYPE`), an oversized file (HTTP 413, which
 * carries no code) names the limit, and everything else gets the translated
 * generic line. Backend prose never reaches the UI.
 */
function refusalMessage(
  err: unknown,
  translate: ReturnType<typeof useTranslation>["translate"],
): string {
  const status = (err as { response?: { status?: number } } | undefined)
    ?.response?.status
  if (status === 413) return translate("MC_DECL_UPLOAD_TOO_LARGE")
  return apiErrorMessage(err, translate, "MC_DECL_UPLOAD_FAILED")
}

/**
 * The declaration intake, as a hook, so the import page and the overview's
 * drop zone run the SAME pipeline: per-file rows with their own outcome, a
 * bounded worker pool, one list invalidation at the end, and the success toast.
 * Callers decide what to do with the outcome (the page navigates back to the
 * overview on a clean run; the overview simply lets its inline card fade).
 */
export function useDeclarationImport() {
  const { translate } = useTranslation()
  const queryClient = useQueryClient()
  const [rows, setRows] = useState<ImportRow[]>([])
  const [isImporting, setIsImporting] = useState(false)

  const patch = (key: string, next: Partial<ImportRow>) =>
    setRows((prev) =>
      prev.map((row) => (row.key === key ? { ...row, ...next } : row)),
    )

  const reset = () => setRows([])

  const importFiles = async (files: File[]): Promise<ImportOutcome | null> => {
    if (!files.length || isImporting) return null

    // The name-based gate is ADVISORY (the backend decides from the bytes) and it
    // runs per file rather than over the batch: a folder with three .docx in it
    // still imports the other forty-seven, and the three are listed by name
    // instead of collapsing the whole drop into one "unsupported" toast.
    const accepted: { key: string; file: File }[] = []
    const seeded: ImportRow[] = files.map((file, i) => {
      const key = `${i}:${file.name}`
      if (!isAcceptedDeclarationFile(file)) {
        return {
          key,
          name: file.name,
          state: "failed",
          error: translate("MC_DECL_DROP_UNSUPPORTED"),
        }
      }
      accepted.push({ key, file })
      return { key, name: file.name, state: "queued" }
    })

    setRows(seeded)
    if (!accepted.length) {
      return { total: files.length, succeeded: 0, failed: files.length }
    }

    setIsImporting(true)

    // Worker pool over one shared cursor (the shape `runBulk` uses for bulk
    // inquiry sends): each worker takes the next file the moment its own is done,
    // so one slow 40 MB workbook cannot stall the files behind it.
    let cursor = 0
    let succeeded = 0
    const drain = async () => {
      while (cursor < accepted.length) {
        const { key, file } = accepted[cursor++]
        patch(key, { state: "uploading" })
        try {
          await uploadDeclarationFile(file)
          succeeded += 1
          patch(key, { state: "done" })
        } catch (err) {
          patch(key, {
            state: "failed",
            error: refusalMessage(err, translate),
          })
        }
      }
    }
    await Promise.all(
      Array.from(
        { length: Math.min(UPLOAD_CONCURRENCY, accepted.length) },
        drain,
      ),
    )

    // ONCE, at the end. Invalidating per upload made a 50-file intake refetch the
    // list (and the stat tiles behind it) fifty times while it was still running.
    if (succeeded > 0) {
      void queryClient.invalidateQueries({ queryKey: ["mc", "declarations"] })
    }

    if (succeeded === files.length) {
      toast.success(
        succeeded === 1
          ? translate("MC_DECL_UPLOAD_TOAST")
          : translate("MC_DECL_UPLOAD_TOAST_MANY", { count: succeeded }),
      )
    }
    setIsImporting(false)
    return {
      total: files.length,
      succeeded,
      failed: files.length - succeeded,
    }
  }

  return { rows, isImporting, importFiles, reset }
}
